import { Injectable } from "@angular/core";
import { ChartOptions, Chart } from 'chart.js/auto';

Chart.defaults.color = '#333';

@Injectable()
export class NWMChartSettings {

  public options: ChartOptions = {};
  public fontSize = 16;

  constructor() {
    this.options = {
      maintainAspectRatio: true,
      responsive: true,
      transitions: {active:{animation:{duration:0}}},
      animation: {duration: 0},
      plugins: {
        tooltip: {enabled: false},
        legend:  {display: false}
      },
      layout: {padding: 0},
      scales: {
        x: {
          border:{display: false},
          grid: {display: false},
          title: {display: false},
          stacked: true,
          ticks: {
            maxRotation:0,
            autoSkip: false,
            font: {
              size : (ctx)=>this.getFontSize(ctx.chart.boxes[0].width),
              family: "'Neue Montreal', 'Geneva', 'sans-serif'",
            },
            padding:0
          }
        },
        y: {
          border:{display: false},
          grid: {display: true},
          title: {display: false},
          stacked: true,
          beginAtZero: true, // looks like beginAtZero doesn't have affect anymore
          ticks: {
            font: {
              size : (ctx)=>this.getFontSize(ctx.chart.boxes[0].width),
              family: "'Neue Montreal', 'Geneva', 'sans-serif'",
            },
            padding:10
          }
        }
      },
    };
  }

  public getFontSize(boxWidth: number) {
    return Math.max(14, Math.min(Math.round(boxWidth / 32), 16));
  }

  public getSuggestedMax(data: number[], multiplier: number, data2?: number[]) {
    if (!Array.isArray(data) || data.length === 0) {
      return undefined;
    }

    const aggregate = data;
    if (Array.isArray(data2)) {
      for (let i = 0; i < data.length && i < aggregate.length; i++) {
        aggregate[i] += data2[i];
      }
    }
    const max = Math.max(1, ...aggregate);
    const min = Math.min(0, ...aggregate);

    return Math.ceil(Math.abs(max - min) * multiplier + max);
  }

  /**
   * it returns a label to be shown for y-axis for the specified tick (number) based on ticks values.
   * It may ignore some labels by setting it to an empty string based on the number of fractions allowed to be shown
   * example:
   * [-500,000 , 0 , 500,000 , 1,000,000] -> [$(0.5M), $0M, $0.5M, $1M] (Because 1M <= Max < 4M => fractions = 1)
   * [-500,000 , 0 , 500,000 , 1,000,000 , 1,500,000 , ...., 10,000,000] -> ['', $0M, '', $1M, '', ..., $10M] (Because Max > 4M => fractions = 0)
   */
  public getYAxisLabelToShow(data: number[], number: number) {

    const max = Math.max(...data);
    const min = Math.min(0, ...data);

    let postfix = '';

    const absMax = Math.abs(max) > Math.abs(min) ? Math.abs(max) : Math.abs(min);

    let fractions = 0;
    if (absMax >= 1000000) {
      number = number / 1000000;
      postfix = 'M';
      if (absMax < 4000000) fractions = 1;
    } else if (absMax >= 100000) {
      number = number / 1000;
      postfix = 'K';
    }
    // Showing $1M instead of $1.2M is misleading. So if we need to ignore fractions, it's better not show these labels in y-axis at all.
    // Even for smaller numbers ($2.5K), it's better not to show labels whose fractions are eliminated because showing wrong value is worse than not showing a value.
    // Note that, based on current formatting, in most cases, there will be at least one value in y-axis whose fractions will not be eliminated
    if ( number * Math.pow(10, fractions) % 1 !== 0) {
      return '';
    }

    // IE hack for negative formatting
    // tslint:disable-next-line:max-line-length
    const text = Math.abs(number).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: fractions, maximumFractionDigits: fractions }) + postfix;
    return number < 0 ? `(${text})` : text;
  }

}

@Injectable()
export class NWMBarChartSettings extends NWMChartSettings{
  constructor() {
    super();
    this.options.animation = {
      duration: 0,
      onComplete: (context) => {
        const ctx = context.chart.ctx;

        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = '#333';
        ctx.font = Math.max(12, this.getFontSize(context.chart.boxes[0].width)).toString() + "px 'Neue Montreal', 'Geneva', 'sans-serif'";

        context.chart.getSortedVisibleDatasetMetas().forEach((metaset, i) => {
          metaset.data.forEach((model, j)=>{
            const data = context.chart.data.datasets[i].data[j];
            const {base} = model.getProps(['base'], true);
            ctx.fillText(
              (data === null || data === undefined) ?
                'N/A' :
                ((+data).toFixed(1) + '%'),
              model.x,
              (+(data || 0) > 0) ?
                model.y :
                base
            );
          })
        });
      }
    };

    this.options.scales!['y']!.ticks!.callback = (value, index) => {
      return index % 2 === 0 ? (value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + '%') : '';
    };
  }
}
